import {
  resolveCanvasFromTemplate,
  canvasToStepStages,
  canvasToBranchPaths,
} from './workflowCanvasModel';
import { normalizeStep } from '@/pages/ipe/workbench/constants/ipeWorkflowStorage';

export const WORKFLOW_PARALLEL_SEP = ' ∥ ';
export const WORKFLOW_PATH_SEP = ' → ';

/** 线性 steps 转为每步一层的 stages */
function linearStepsToStages(steps) {
  if (!Array.isArray(steps)) return [];
  return steps.map((s) => [normalizeStep(s)]).filter((stage) => stage[0]?.navId);
}

/**
 * 工作流模块必须串行：上一个模块整批完成后再进入下一个。
 * 不再把 AI 文案提前并进首个图片阶段，否则会退回「每组印花各建一个批次」。
 */
function promoteIndependentTextSteps(stages) {
  return Array.isArray(stages) ? stages : [];
}

function stepIdentityKey(step) {
  if (!step) return '';
  if (step.id) return `id:${step.id}`;
  return `${step.navId || ''}|${step.label || ''}`;
}

/** 从模板解析分支路径（展示/执行均优先使用，能区分「姿势裂变」属于哪条支路） */
export function resolveTemplateBranchPaths(template) {
  if (!template || typeof template !== 'object') return [];

  if (template.canvas && typeof template.canvas === 'object') {
    const paths = canvasToBranchPaths(resolveCanvasFromTemplate(template));
    if (paths.length > 0) return paths;
  }

  const steps = (template.steps || [])
    .map(normalizeStep)
    .filter((s) => s?.navId && s.navId !== 'sourcingHub');
  if (steps.length === 0) return [];
  return [steps];
}

/**
 * 展示用分支路径：与执行路径不同，这里保留「插件采集」等素材入口节点，
 * 让任务/模板的流程链能看到素材来源步骤（此前采集节点在流程链里完全不显示）。
 */
export function resolveDisplayBranchPaths(template) {
  if (!template || typeof template !== 'object') return [];

  if (template.canvas && typeof template.canvas === 'object') {
    const paths = canvasToBranchPaths(resolveCanvasFromTemplate(template));
    if (paths.length > 0) return paths;
  }

  const steps = (template.steps || []).map(normalizeStep).filter((s) => s?.navId);
  if (steps.length === 0) return [];
  return [steps];
}

/** 从模板解析执行阶段（并行层，用于进度条展平顺序） */
export function resolveTemplateStepStages(template) {
  if (!template || typeof template !== 'object') return [];

  if (template.canvas && typeof template.canvas === 'object') {
    const fromCanvas = canvasToStepStages(resolveCanvasFromTemplate(template));
    if (fromCanvas.length > 0) return promoteIndependentTextSteps(fromCanvas);
  }

  if (Array.isArray(template.stepStages) && template.stepStages.length > 0) {
    const stages = template.stepStages
      .map((stage) => (Array.isArray(stage) ? stage.map(normalizeStep).filter((s) => s?.navId) : []))
      .filter((stage) => stage.length > 0);
    if (stages.length > 0) return promoteIndependentTextSteps(stages);
  }

  return promoteIndependentTextSteps(linearStepsToStages(template.steps || []));
}

/** 可执行流水线步骤（展平，不含插件采集） */
export function resolveTemplatePipelineSteps(template) {
  return resolveTemplateStepStages(template)
    .flat()
    .filter((s) => s.navId !== 'sourcingHub');
}

/** flat 下标：按模板步骤顺序定位 */
export function resolveWorkflowStepFlatIndex(template, step) {
  const flat = resolveTemplatePipelineSteps(template);
  const key = stepIdentityKey(step);
  const idx = flat.findIndex((s) => stepIdentityKey(s) === key);
  return idx >= 0 ? idx : flat.findIndex((s) => s.navId === step?.navId && s.label === step?.label);
}

/**
 * 执行计划：独立分支并行；若多路径共享末尾步骤则先并行前缀再汇合执行 suffix
 * 例：[[模特试衣, 姿势裂变], [AI文案]] → parallel
 * 例：[[A, C], [B, C]] → join，branches=[[A],[B]], suffix=[C]
 */
export function resolveTemplateExecutionPlan(template) {
  const paths = resolveTemplateBranchPaths(template)
    .map((path) => path.filter((s) => s?.navId && s.navId !== 'sourcingHub'))
    .filter((path) => path.length > 0);

  if (paths.length <= 1) {
    return {
      mode: 'linear',
      branches: paths.length ? paths : [],
      suffix: [],
    };
  }

  let suffixLen = 0;
  const minLen = Math.min(...paths.map((p) => p.length));
  for (let i = 1; i <= minLen; i += 1) {
    const key = stepIdentityKey(paths[0][paths[0].length - i]);
    const allMatch = paths.every((p) => stepIdentityKey(p[p.length - i]) === key);
    if (allMatch) suffixLen = i;
    else break;
  }

  if (suffixLen > 0) {
    const suffix = paths[0].slice(-suffixLen);
    const branches = paths
      .map((p) => p.slice(0, -suffixLen))
      .filter((p) => p.length > 0);
    if (branches.length > 1) {
      return { mode: 'join', branches, suffix };
    }
  }

  return { mode: 'parallel', branches: paths, suffix: [] };
}

/** 将 flat 下标映射到阶段信息 */
export function locateStepInStages(stages, flatIndex) {
  let offset = 0;
  for (let stageIndex = 0; stageIndex < stages.length; stageIndex += 1) {
    const stage = stages[stageIndex];
    if (flatIndex < offset + stage.length) {
      return {
        stageIndex,
        indexInStage: flatIndex - offset,
        stageStartIndex: offset,
        stage,
        isParallel: stage.length > 1,
      };
    }
    offset += stage.length;
  }
  return null;
}

/** 续跑时对齐到阶段起点（并行阶段内任一步续跑则整阶段重跑） */
export function alignResumeStepIndex(stages, startStepIndex) {
  const safe = Math.max(0, Number(startStepIndex) || 0);
  const loc = locateStepInStages(stages, safe);
  if (!loc) return safe;
  if (loc.isParallel && loc.indexInStage > 0) {
    return loc.stageStartIndex;
  }
  return safe;
}

/**
 * 人工审核通过后应继续执行的首个 flat 步骤下标。
 * 若审核步骤位于并行阶段，同阶段其它步骤（如 AI 文案）通常已跑完，应跳到下一阶段而非 reviewStep+1。
 */
export function resolveWorkflowReviewContinueStepIndex(template, reviewStepIndex) {
  const stages = resolveTemplateStepStages(template);
  const safeReview = Math.max(0, Number(reviewStepIndex) || 0);
  const loc = locateStepInStages(stages, safeReview);
  if (!loc) return safeReview + 1;
  if (loc.isParallel) {
    return loc.stageStartIndex + loc.stage.length;
  }
  return safeReview + 1;
}

/** 流程链展示：每条支路内用 →，支路之间用 ∥ */
export function formatWorkflowChainDisplay(templateOrPaths, { pathSep = WORKFLOW_PATH_SEP, branchSep = WORKFLOW_PARALLEL_SEP } = {}) {
  const paths = Array.isArray(templateOrPaths)
    ? templateOrPaths
    : resolveDisplayBranchPaths(templateOrPaths);
  if (!paths.length) return '';
  return paths
    .map((path) => path.map((s) => s.label || s.navId).filter(Boolean).join(pathSep))
    .filter(Boolean)
    .join(branchSep);
}

/** 流程链分组：每条支路一组（支路内顺序，支路间并行） */
export function buildWorkflowChainNodeGroups(templateOrPaths) {
  const paths = Array.isArray(templateOrPaths)
    ? templateOrPaths
    : resolveDisplayBranchPaths(templateOrPaths);
  return paths
    .map((steps, branchIndex) => ({
      steps: Array.isArray(steps) ? steps.filter((s) => s?.navId) : [],
      branchIndex,
      isBranch: paths.length > 1,
    }))
    .filter((group) => group.steps.length > 0);
}

/** 流程链 tooltip */
export function formatWorkflowChainTooltip(template) {
  return buildWorkflowChainNodeGroups(template)
    .map((group) =>
      group.steps
        .map((step) => step.label || step.navId || '')
        .join(WORKFLOW_PATH_SEP),
    )
    .join(WORKFLOW_PARALLEL_SEP);
}

/** 分支执行专用模板：剥离 canvas/stepStages 全图，避免子流水线误跑整张图 */
export function buildBranchExecutionTemplate(template, branchSteps) {
  const steps = (branchSteps || [])
    .map(normalizeStep)
    .filter((s) => s?.navId && s.navId !== 'sourcingHub');
  return {
    id: template?.id,
    name: template?.name,
    group: template?.group,
    steps,
    stepStages: steps.map((s) => [s]),
  };
}
